import { FormEvent, MouseEvent, useEffect, useMemo, useState } from "react";
import type { Listing } from "./data";

type MarketplaceProps = {
  initialListings: Listing[];
};

const categories = [
  "Kaikki",
  "Kupari",
  "Messinki",
  "Rauta",
  "Alumiini",
  "Kaapeli",
  "Ruostumaton",
];

const categorySymbols: Record<string, string> = {
  Kupari: "Cu",
  Messinki: "Ms",
  Rauta: "Fe",
  Alumiini: "Al",
  Kaapeli: "Ka",
  Ruostumaton: "Rst",
};

const materialClass = (category: string) =>
  `material-${category
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z]/g, "")}`;

const euros = (value: number) =>
  new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);

const ageLabel = (timestamp: number) => {
  const hours = Math.max(1, Math.floor((Date.now() - timestamp) / 3_600_000));
  if (hours < 24) return `${hours} h sitten`;
  const days = Math.floor(hours / 24);
  return `${days} pv sitten`;
};

export default function Marketplace({ initialListings }: MarketplaceProps) {
  const [listings, setListings] = useState(initialListings);
  const [category, setCategory] = useState("Kaikki");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState<Listing | null>(null);
  const [modal, setModal] = useState<"publish" | "offer" | null>(null);
  const [toast, setToast] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingListings, setLoadingListings] = useState(true);
  const [selectedImageName, setSelectedImageName] = useState("");

  useEffect(() => {
    fetch("/api/listings")
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (Array.isArray(data?.listings)) setListings(data.listings);
      })
      .catch(() => undefined)
      .finally(() => setLoadingListings(false));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setModal(null);
        setSelected(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const filteredListings = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("fi");
    const filtered = listings.filter((listing) => {
      const matchesCategory =
        category === "Kaikki" || listing.category === category;
      const searchable =
        `${listing.title} ${listing.category} ${listing.location} ${listing.quantity}`.toLocaleLowerCase(
          "fi",
        );
      return matchesCategory && (!query || searchable.includes(query));
    });

    return [...filtered].sort((a, b) => {
      if (sort === "price-low") return a.askingPrice - b.askingPrice;
      if (sort === "price-high") return b.askingPrice - a.askingPrice;
      if (sort === "offers") return b.offerCount - a.offerCount;
      return b.createdAt - a.createdAt;
    });
  }, [category, listings, search, sort]);

  const marketLeader = useMemo(() => {
    if (!listings.length) return null;

    const totals = listings.reduce<Record<string, number>>((current, listing) => {
      current[listing.category] =
        (current[listing.category] ?? 0) + listing.offerCount;
      return current;
    }, {});
    const categoryName = Object.entries(totals).sort(
      ([categoryA, offersA], [categoryB, offersB]) =>
        offersB - offersA || categoryA.localeCompare(categoryB, "fi"),
    )[0]?.[0];

    return categoryName
      ? { category: categoryName, offers: totals[categoryName] }
      : null;
  }, [listings]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 4200);
  };

  const openPublish = () => {
    setSelectedImageName("");
    setModal("publish");
  };

  const openOffer = (listing: Listing) => {
    setSelected(listing);
    setModal("offer");
  };

  const closeOverlay = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      setModal(null);
      setSelected(null);
    }
  };

  const submitListing = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSubmitting(true);
    try {
      const response = await fetch("/api/listings", {
        method: "POST",
        body: new FormData(event.currentTarget),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Julkaisu epäonnistui.");
      setListings((current) => [data.listing, ...current]);
      setModal(null);
      showToast("Myyntierä julkaistiin onnistuneesti.");
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : "Julkaisu epäonnistui.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitOffer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;

    const fields = new FormData(event.currentTarget);
    const amount = Number(fields.get("amount"));
    const buyerName = String(fields.get("name") ?? "");
    const buyerEmail = String(fields.get("email") ?? "");
    const buyerPhone = String(fields.get("phone") ?? "");
    const message = String(fields.get("message") ?? "");
    setSubmitting(true);
    try {
      const response = await fetch(`/api/listings/${selected.id}/offers`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          amount,
          name: buyerName,
          email: buyerEmail,
          message,
          phone: buyerPhone,
          consent: fields.get("buyerConsent") === "on",
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Tarjous epäonnistui.");
      setListings((current) =>
        current.map((listing) =>
          listing.id === selected.id
            ? {
                ...listing,
                offerCount: data.offerCount,
                highestOffer: data.highestOffer,
              }
            : listing,
        ),
      );
      setModal(null);
      setSelected(null);
      const subject = encodeURIComponent(`Tarjous: ${selected.title}`);
      const emailBody = encodeURIComponent(
        `Hei!\n\nTarjoan erästä ”${selected.title}” ${euros(amount)}.\n\n${message}\n\nYstävällisin terveisin,\n${buyerName}\n${buyerEmail}\n${buyerPhone}`,
      );
      window.location.href = `mailto:${selected.contactEmail}?subject=${subject}&body=${emailBody}`;
      showToast("Tarjous tallennettiin. Lähetä avautuva sähköposti myyjälle.");
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : "Tarjous epäonnistui.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <div className="topline">
        <div className="container topline-inner">
          <span>Suomalainen metalli-ilmoitusten markkinapaikka</span>
          <span className="topline-trust">
            <i aria-hidden="true" /> Ei käyttäjätiliä · Maksuton ilmoitus
          </span>
        </div>
      </div>

      <header className="site-header">
        <div className="container header-inner">
          <a className="brand" href="#" aria-label="RMK etusivu">
            <span className="brand-mark">RMK</span>
            <span className="brand-name">
              Romumetalli
              <br />
              kauppa
            </span>
          </a>

          <nav aria-label="Päänavigaatio">
            <a href="#erat">Myyntierät</a>
            <a href="#toiminta">Näin se toimii</a>
            <a href="#turva">Turvallinen kauppa</a>
          </nav>

          <div className="header-actions">
            <button className="button button-dark header-cta" onClick={openPublish}>
              <span>＋</span> Myy metallia
            </button>
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="container hero-grid">
          <div className="hero-copy">
            <div className="eyebrow">
              <span className="live-dot" /> Metalli vaihtaa omistajaa täällä
            </div>
            <h1>
              Metalli kiertoon.
              <br />
              <em>Paras tarjous voittaa.</em>
            </h1>
            <p>
              Julkaise romumetallieräsi muutamassa minuutissa ja vastaanota
              tarjouksia ostajilta ympäri Suomen.
            </p>
            <div className="hero-actions">
              <button className="button button-orange" onClick={openPublish}>
                Ilmoita myyntierä <span aria-hidden="true">→</span>
              </button>
              <a className="button button-ghost" href="#erat">
                Selaa eriä <span aria-hidden="true">↓</span>
              </a>
            </div>
            <div className="proof-row" aria-label="Palvelun tärkeimmät edut">
              <span>✓ Ei rekisteröitymistä</span>
              <span>✓ Kaiken kokoiset erät</span>
              <span>✓ Tarjoukset suoraan myyjälle</span>
            </div>
          </div>

          <div className="hero-market" aria-label="Markkinapaikan tilanne">
            <div className="market-head">
              <span>RMK / MARKKINA NYT</span>
              <span className="market-time">PÄIVITTYVÄ</span>
            </div>
            <div className="market-feature">
              <div
                className={`material-orbit ${
                  marketLeader ? materialClass(marketLeader.category) : "empty-orbit"
                }`}
                aria-hidden="true"
              >
                <span>
                  {marketLeader
                    ? categorySymbols[marketLeader.category] || "RMK"
                    : "RMK"}
                </span>
              </div>
              <div>
                <small>
                  {marketLeader ? "Suosituin materiaali" : "Markkinapaikka avattu"}
                </small>
                <strong>{marketLeader?.category ?? "Ensimmäinen erä?"}</strong>
                <p>
                  {marketLeader
                    ? marketLeader.offers
                      ? `${marketLeader.offers} tarjousta avoimissa erissä`
                      : "Ensimmäisiä tarjouksia odotetaan"
                    : "Julkaise se maksutta muutamassa minuutissa"}
                </p>
              </div>
            </div>
            <div className="market-stats">
              <div>
                <span>Avoimia eriä</span>
                <strong>{listings.length}</strong>
              </div>
              <div>
                <span>Tarjouksia</span>
                <strong>
                  {listings.reduce((sum, item) => sum + item.offerCount, 0)}
                </strong>
              </div>
              <div>
                <span>Paikkakuntia</span>
                <strong>{new Set(listings.map((item) => item.location)).size}</strong>
              </div>
            </div>
            <div className="market-note">
              <span className="pulse" />
              {listings.length
                ? `Uusin erä lisätty ${ageLabel(listings[0].createdAt)}`
                : "Avoimia eriä ei vielä ole"}
            </div>
          </div>
        </div>
      </section>

      <section className="browse-section" id="erat">
        <div className="container">
          <div className="section-heading">
            <div>
              <span className="section-kicker">AVOIMET MYYNTIERÄT</span>
              <h2>Löydä seuraava eräsi</h2>
            </div>
            <p>
              Tee tarjous suoraan myyjälle. Kaupan ehdoista ja noudosta sovitaan
              ostajan ja myyjän kesken.
            </p>
          </div>

          <div className="browse-tools">
            <label className="search-box">
              <span aria-hidden="true">⌕</span>
              <input
                aria-label="Hae myyntieriä"
                placeholder="Hae metallia tai paikkakuntaa"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <label className="sort-box">
              <span>Lajittelu</span>
              <select
                aria-label="Lajittele ilmoitukset"
                value={sort}
                onChange={(event) => setSort(event.target.value)}
              >
                <option value="newest">Uusimmat ensin</option>
                <option value="offers">Eniten tarjouksia</option>
                <option value="price-low">Hinta: pienin ensin</option>
                <option value="price-high">Hinta: suurin ensin</option>
              </select>
            </label>
          </div>

          <div className="category-row" aria-label="Metallikategoriat">
            {categories.map((item) => (
              <button
                className={category === item ? "category active" : "category"}
                key={item}
                onClick={() => setCategory(item)}
                aria-pressed={category === item}
              >
                {item !== "Kaikki" && (
                  <span aria-hidden="true">{categorySymbols[item]}</span>
                )}
                {item}
              </button>
            ))}
          </div>

          <div className="result-meta">
            <span>
              <strong>{filteredListings.length}</strong> avointa erää
            </span>
            {(category !== "Kaikki" || search) && (
              <button
                onClick={() => {
                  setCategory("Kaikki");
                  setSearch("");
                }}
              >
                Tyhjennä rajaukset ×
              </button>
            )}
          </div>

          {loadingListings ? (
            <div className="loading-state" role="status">
              <span aria-hidden="true" />
              Ladataan avoimia myyntieriä…
            </div>
          ) : filteredListings.length ? (
            <div className="listing-grid">
              {filteredListings.map((listing) => (
                <article
                  className="listing-card"
                  key={listing.id}
                  onClick={() => setSelected(listing)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelected(listing);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Avaa ilmoitus: ${listing.title}`}
                >
                  <div
                    className={`listing-image ${materialClass(listing.category)}`}
                  >
                    {listing.imageUrl ? (
                      // User-uploaded listing photos are served from RMK storage.
                      <img src={listing.imageUrl} alt={listing.title} />
                    ) : (
                      <>
                        <span className="material-symbol" aria-hidden="true">
                          {categorySymbols[listing.category] || "RMK"}
                        </span>
                        <span className="material-caption">
                          {listing.category} · ei kuvaa
                        </span>
                      </>
                    )}
                    <span className="listing-age">{ageLabel(listing.createdAt)}</span>
                    <span className="listing-category">{listing.category}</span>
                  </div>
                  <div className="listing-body">
                    <div className="listing-location">
                      <span aria-hidden="true">●</span> {listing.location}
                    </div>
                    <h3>{listing.title}</h3>
                    <p className="quantity">{listing.quantity}</p>
                    <div className="price-row">
                      <div>
                        <span>Pyynti</span>
                        <strong>{euros(listing.askingPrice)}</strong>
                      </div>
                      <button
                        className="offer-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openOffer(listing);
                        }}
                      >
                        Tarjoa →
                      </button>
                    </div>
                    <div className="offer-meta">
                      <span>
                        {listing.offerCount}{" "}
                        {listing.offerCount === 1 ? "tarjous" : "tarjousta"}
                      </span>
                      {listing.highestOffer && (
                        <span>Korkein {euros(listing.highestOffer)}</span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <span>RMK</span>
              <h3>
                {listings.length
                  ? "Ei osumia tällä rajauksella"
                  : "Ole ensimmäinen myyjä"}
              </h3>
              <p>
                {listings.length
                  ? "Kokeile toista hakusanaa tai näytä kaikki metallierät."
                  : "RMK on valmis kaupankäyntiin. Julkaise ensimmäinen metallierä maksutta ilman rekisteröitymistä."}
              </p>
              <button
                className="button button-dark"
                onClick={
                  listings.length
                    ? () => {
                        setCategory("Kaikki");
                        setSearch("");
                      }
                    : openPublish
                }
              >
                {listings.length ? "Näytä kaikki erät" : "Julkaise ensimmäinen erä"}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="how-section" id="toiminta">
        <div className="container">
          <div className="how-intro">
            <span className="section-kicker">MYYJÄLLE HELPPO</span>
            <h2>Romu rahaksi kolmella askeleella.</h2>
            <p>
              Kuvaa erä, kerro tärkeimmät tiedot ja valitse sinulle paras
              tarjous. RMK ei tule kaupan väliin.
            </p>
          </div>
          <div className="steps">
            <article>
              <span className="step-number">01</span>
              <div className="step-icon camera-icon" aria-hidden="true" />
              <h3>Julkaise erä</h3>
              <p>Lisää kuva, materiaalitiedot, määrä, hinta ja noutopaikka.</p>
            </article>
            <article>
              <span className="step-number">02</span>
              <div className="step-icon bid-icon" aria-hidden="true">
                €
              </div>
              <h3>Vastaanota tarjoukset</h3>
              <p>Ostajat jättävät tarjouksensa ja viestinsä suoraan erälle.</p>
            </article>
            <article>
              <span className="step-number">03</span>
              <div className="step-icon deal-icon" aria-hidden="true">
                ✓
              </div>
              <h3>Sovi kaupat</h3>
              <p>Valitse sopiva tarjous ja sovi maksu sekä nouto ostajan kanssa.</p>
            </article>
          </div>
          <button className="button button-orange how-cta" onClick={openPublish}>
            Aloita myynti – se on maksutonta →
          </button>
        </div>
      </section>

      <section className="safety-section" id="turva">
        <div className="container safety-grid">
          <div>
            <span className="section-kicker">REILUN KAUPAN PELISÄÄNNÖT</span>
            <h2>Tee metallikaupat turvallisesti.</h2>
          </div>
          <div className="safety-list">
            <div>
              <span>1</span>
              <p>
                <strong>Tarkista erä ennen maksua.</strong> Pyydä tarvittaessa
                lisäkuvia, punnituskuitti ja materiaalin laatutiedot.
              </p>
            </div>
            <div>
              <span>2</span>
              <p>
                <strong>Sovi kaikki kirjallisesti.</strong> Kirjaa hinta,
                maksutapa, nouto ja vastuut viestiin tai kauppakirjaan.
              </p>
            </div>
            <div>
              <span>3</span>
              <p>
                <strong>Älä maksa kiireessä.</strong> RMK ei pyydä maksua
                kaupan vahvistamisesta tai lähetä maksulinkkejä puolestasi.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <div className="container footer-grid">
          <div>
            <a className="brand brand-light" href="#">
              <span className="brand-mark">RMK</span>
              <span className="brand-name">Romumetallikauppa</span>
            </a>
            <p>Metalli kiertoon. Reilu kauppa suoraan tekijöiden kesken.</p>
          </div>
          <div className="footer-links">
            <a href="#erat">Myyntierät</a>
            <a href="#toiminta">Näin se toimii</a>
            <a href="#turva">Turvallisuus</a>
          </div>
          <div className="footer-action">
            <p>Onko sinulla metallia myytävänä?</p>
            <button className="button button-orange" onClick={openPublish}>
              Lisää myyntierä →
            </button>
          </div>
        </div>
        <div className="container footer-bottom">
          <span>© {new Date().getFullYear()} Romumetallikauppa / RMK</span>
          <div className="footer-legal-links">
            <a href="/kayttoehdot/">Käyttöehdot</a>
            <a href="/tietosuojaseloste/">Tietosuojaseloste</a>
          </div>
        </div>
      </footer>

      {selected && !modal && (
        <div className="modal-overlay" onMouseDown={closeOverlay}>
          <section
            className="modal listing-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="listing-title"
          >
            <button
              className="modal-close"
              aria-label="Sulje"
              onClick={() => setSelected(null)}
            >
              ×
            </button>
            <div
              className={`modal-material ${materialClass(selected.category)}`}
            >
              {selected.imageUrl ? (
                <img src={selected.imageUrl} alt={selected.title} />
              ) : (
                <span>{categorySymbols[selected.category] || "RMK"}</span>
              )}
            </div>
            <div className="listing-modal-content">
              <div className="modal-tags">
                <span>{selected.category}</span>
                <span>{selected.location}</span>
                <span>{selected.quantity}</span>
              </div>
              <h2 id="listing-title">{selected.title}</h2>
              <p className="modal-description">{selected.description}</p>
              <div className="seller-card">
                <div className="seller-avatar">
                  {selected.sellerName.slice(0, 1)}
                </div>
                <div>
                  <small>Myyjä</small>
                  <strong>{selected.sellerName}</strong>
                  <span className="seller-links">
                    <a href={`tel:${selected.contactPhone}`}>
                      {selected.contactPhone}
                    </a>
                    <span aria-hidden="true"> · </span>
                    <a href={`mailto:${selected.contactEmail}`}>
                      {selected.contactEmail}
                    </a>
                  </span>
                </div>
              </div>
              <div className="modal-price-row">
                <div>
                  <span>Pyyntihinta</span>
                  <strong>{euros(selected.askingPrice)}</strong>
                </div>
                <div>
                  <span>Tarjouksia</span>
                  <strong>{selected.offerCount}</strong>
                </div>
                <button
                  className="button button-orange"
                  onClick={() => setModal("offer")}
                >
                  Jätä tarjous →
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      {modal === "offer" && selected && (
        <div className="modal-overlay" onMouseDown={closeOverlay}>
          <section
            className="modal form-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="offer-title"
          >
            <button
              className="modal-close"
              aria-label="Sulje"
              onClick={() => {
                setModal(null);
                setSelected(null);
              }}
            >
              ×
            </button>
            <span className="modal-kicker">TARJOUS ERÄSTÄ</span>
            <h2 id="offer-title">{selected.title}</h2>
            <div className="offer-summary">
              <span>Pyynti {euros(selected.askingPrice)}</span>
              <span>
                {selected.offerCount}{" "}
                {selected.offerCount === 1 ? "tarjous" : "tarjousta"}
              </span>
            </div>
            <div className="inline-auth">
              <strong>Tarjouksen jättäminen ei vaadi käyttäjätiliä.</strong>
              <p>
                Tiedot tallennetaan tarjoukseen ja sähköpostiohjelmasi avataan
                viestin toimittamiseksi myyjälle.
              </p>
            </div>
            <form onSubmit={submitOffer}>
              <label>
                Tarjouksesi (€)
                <input
                  name="amount"
                  type="number"
                  min="1"
                  required
                  defaultValue={
                    selected.highestOffer
                      ? selected.highestOffer + 50
                      : selected.askingPrice
                  }
                />
              </label>
              <div className="form-grid">
                <label>
                  Nimi
                  <input name="name" required maxLength={80} placeholder="Etunimi Sukunimi" />
                </label>
                <label>
                  Sähköposti
                  <input
                    name="email"
                    type="email"
                    required
                    maxLength={120}
                    placeholder="nimi@yritys.fi"
                  />
                </label>
              </div>
              <label>
                Puhelinnumero
                <input
                  name="phone"
                  type="tel"
                  maxLength={40}
                  placeholder="040 123 4567"
                  required
                />
              </label>
              <label>
                Viesti myyjälle
                <textarea
                  name="message"
                  rows={4}
                  placeholder="Kerro esimerkiksi noudosta ja maksutavasta."
                  required
                />
              </label>
              <label className="consent-row">
                <input name="buyerConsent" type="checkbox" required />
                <span>
                  Hyväksyn yhteystietojeni tallentamisen ja välittämisen tämän
                  erän myyjälle.
                </span>
              </label>
              <button
                className="button button-orange button-full"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Lähetetään…" : "Lähetä tarjous →"}
              </button>
              <p className="form-legal">
                Muista lähettää avautuva sähköposti. Voit ottaa myyjään yhteyttä
                myös suoraan: {selected.contactEmail}. RMK ei ole kaupan osapuoli.
              </p>
            </form>
          </section>
        </div>
      )}

      {modal === "publish" && (
        <div className="modal-overlay" onMouseDown={closeOverlay}>
          <section
            className="modal publish-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="publish-title"
          >
            <button
              className="modal-close"
              aria-label="Sulje"
              onClick={() => setModal(null)}
            >
              ×
            </button>
            <div className="publish-head">
              <div>
                <span className="modal-kicker">UUSI MYYNTIERÄ</span>
                <h2 id="publish-title">Mitä metallia myyt?</h2>
              </div>
              <span className="free-badge">Maksuton</span>
            </div>
            <div className="inline-auth">
              <strong>Julkaisu onnistuu heti ilman rekisteröitymistä.</strong>
              <p>Täytä erän tiedot ja omat yhteystietosi – siinä kaikki.</p>
            </div>
            <form className="publish-form" onSubmit={submitListing}>
              <label className="honeypot" aria-hidden="true">
                Verkkosivusto
                <input
                  name="companyWebsite"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </label>
              <label className="image-upload">
                <input
                  name="image"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(event) =>
                    setSelectedImageName(event.currentTarget.files?.[0]?.name ?? "")
                  }
                />
                <span className="upload-plus">＋</span>
                <strong>{selectedImageName || "Lisää erästä kuva"}</strong>
                <small>
                  {selectedImageName
                    ? "Kuva valittu"
                    : "Suositeltu · JPG, PNG tai WebP · enintään 8 Mt"}
                </small>
              </label>
              <div className="form-grid">
                <label className="span-2">
                  Ilmoituksen otsikko
                  <input
                    name="title"
                    required
                    maxLength={90}
                    placeholder="Esim. Puhdasta kupariputkea, lajiteltu erä"
                  />
                </label>
                <label>
                  Metalli
                  <select name="category" required defaultValue="">
                    <option value="" disabled>
                      Valitse materiaali
                    </option>
                    {categories.slice(1).map((item) => (
                      <option value={item} key={item}>
                        {item}
                      </option>
                    ))}
                    <option value="Muu">Muu metalli</option>
                  </select>
                </label>
                <label>
                  Paikkakunta
                  <input
                    name="location"
                    required
                    maxLength={60}
                    placeholder="Esim. Turku"
                  />
                </label>
                <label>
                  Erän koko
                  <input
                    name="quantity"
                    required
                    maxLength={60}
                    placeholder="Esim. noin 500 kg"
                  />
                </label>
                <label>
                  Pyyntihinta (€)
                  <input name="askingPrice" type="number" min="0" required />
                </label>
                <label className="span-2">
                  Kuvaus
                  <textarea
                    name="description"
                    rows={4}
                    required
                    maxLength={1200}
                    placeholder="Kerro materiaalin kunto, puhtaus, lastausmahdollisuus ja muut ostajalle tärkeät tiedot."
                  />
                </label>
                <label>
                  Yhteyshenkilö
                  <input
                    name="contactName"
                    required
                    maxLength={90}
                    placeholder="Nimi tai yritys"
                  />
                </label>
                <label>
                  Puhelinnumero
                  <input
                    name="contactPhone"
                    type="tel"
                    required
                    maxLength={40}
                    placeholder="040 123 4567"
                  />
                </label>
                <label className="span-2">
                  Yhteyssähköposti
                  <input
                    name="contactEmail"
                    type="email"
                    required
                    maxLength={120}
                    placeholder="nimi@yritys.fi"
                  />
                </label>
              </div>
              <label className="consent-row">
                <input name="contactConsent" type="checkbox" required />
                <span>
                  Hyväksyn yhteystietojeni julkisen näyttämisen ilmoituksessa ja
                  tietojen tallentamisen. Ilmoitus näkyy enintään 5 päivää.
                </span>
              </label>
              <button
                className="button button-orange button-full"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Julkaistaan…" : "Julkaise myyntierä →"}
              </button>
            </form>
          </section>
        </div>
      )}

      {toast && (
        <div className="toast" role="status">
          <span>✓</span> {toast}
        </div>
      )}
    </main>
  );
}
